/**
 * authService.js — Authentication API calls (JWT-based).
 *
 * All auth endpoints go through /api/manager/auth/*
 */

// Local 401 notifier — fires the same window event AuthContext listens
// to. Kept inline so this file doesn't depend on api.js (which would
// create a circular import once api.js eventually imports from here).
function _notifyExpired() {
  try {
    window.dispatchEvent(new CustomEvent('auth:expired', {
      detail: { resumeUrl: window.location.pathname + window.location.search },
    }));
  } catch { /* no-op */ }
}

const AUTH_BASE = '/api/manager/auth';

/**
 * Login with username + password → JWT token
 */
export async function loginUser(username, password) {
  const formData = new URLSearchParams();
  formData.append('username', username);
  formData.append('password', password);

  const response = await fetch(`${AUTH_BASE}/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: formData,
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    const detail = data.detail || 'Login failed';
    const error = new Error(detail);
    error.status = response.status;
    error.detail = detail;
    throw error;
  }

  return await response.json();
}

/**
 * Register a new user
 */
export async function registerUser({ email, username, password, full_name, timezone }) {
  const response = await fetch(`${AUTH_BASE}/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, username, password, full_name, timezone }),
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.detail || 'Registration failed');
  }

  return await response.json();
}

/**
 * Get current user profile (validates token)
 */
export async function getCurrentUser(token) {
  const response = await fetch(`${AUTH_BASE}/me`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    if (response.status === 401) _notifyExpired();
    throw new Error('Token invalid');
  }

  return await response.json();
}

/**
 * Update current user's profile
 */
export async function updateProfile(token, { full_name, timezone }) {
  const formData = new URLSearchParams();
  if (full_name != null) formData.append('full_name', full_name);
  if (timezone != null) formData.append('timezone', timezone);

  const response = await fetch(`${AUTH_BASE}/me`, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: formData,
  });

  if (!response.ok) {
    if (response.status === 401) _notifyExpired();
    const data = await response.json().catch(() => ({}));
    throw new Error(data.detail || 'Update failed');
  }

  return await response.json();
}

/**
 * Get public validation policy (for signup form)
 */
export async function getValidationPolicy() {
  const response = await fetch(`${AUTH_BASE.replace('/auth', '')}/api/validation-policy/public`);
  if (!response.ok) return null;
  return await response.json();
}

/**
 * Check whether Microsoft Entra SSO is enabled on the backend.
 * Returns { enabled: boolean }.
 */
export async function getEntraSsoConfig() {
  try {
    const response = await fetch(`${AUTH_BASE}/entra/config`);
    if (!response.ok) return { enabled: false };
    return await response.json();
  } catch {
    return { enabled: false };
  }
}

/**
 * Start the Microsoft Entra SSO flow by navigating the browser to the
 * backend /auth/entra/login endpoint, which will 302 to Microsoft.
 * `nextPath` is preserved through the round-trip so we land back on the
 * original page after sign-in.
 */
export function startEntraLogin(nextPath = '/') {
  // Validate nextPath is a relative same-origin path to prevent open redirect.
  const safePath = (nextPath && /^\/(?!\/)[^\s]*$/.test(nextPath)) ? nextPath : '/';
  const url = `${AUTH_BASE}/entra/login?next=${encodeURIComponent(safePath)}`;
  window.location.href = url;
}

/**
 * Resend email verification
 */
export async function resendVerification(username) {
  const formData = new URLSearchParams();
  formData.append('username', username);

  const response = await fetch(`${AUTH_BASE}/resend-verification`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: formData,
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.detail || 'Failed to resend verification');
  }

  return await response.json();
}

/**
 * Get user email by username (for verification dialog)
 */
export async function getUserEmail(username) {
  const formData = new URLSearchParams();
  formData.append('username', username);

  const response = await fetch(`${AUTH_BASE}/get-user-email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: formData,
  });

  if (!response.ok) return null;
  const data = await response.json();
  return data.email;
}

/**
 * Verify email token
 */
export async function verifyEmailToken(token) {
  const response = await fetch(`${AUTH_BASE}/verify-email/confirm`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.detail || 'Verification failed');
  }

  return await response.json();
}

export default {
  loginUser,
  registerUser,
  getCurrentUser,
  updateProfile,
  getValidationPolicy,
  resendVerification,
  getUserEmail,
  verifyEmailToken,
};
